import { ActivityIndicator, Divider, Text } from "react-native-paper";
import { ScreenProps, Address } from "./types";
import { ActionsContainer, Button, PopOver, ScreenContainer, ScrollableContainer } from "../components";
import DisplayAddress from "../components/Address";
import { Platform, View } from "react-native";
import { ApolloCache, FetchResult, Reference, gql, makeVar, useMutation, useQuery, useReactiveVar } from "@apollo/client";
import DatePickerInput from "../components/DatePickerInput";
import { Moment } from "moment";
import { useEffect, useState } from "react";
import useSnackBar from "../hooks/useSnackBar";
import { readableDate } from "../utils/dateUtils";
import UnsavedChangesModal from "../components/UnsavedChangesModal";
import SentForReviewScreen from "./AccountScreen/SentForReviewModal";
import { ChangeStatusEnum } from "./EmploymentEditScreen/types";
import FutureDateEmploymentPopOver, { getFutureDate } from "../components/FutureDateEmploymentPopOver";

interface Employment {
  id: string;
  person: { id: string };
  facility: { name: string };
}

export const removedContactEmployment = makeVar<Employment | null>(null);

export const navigationTitle = "Remove Employment";

export const EMPLOYMENT_QUERY = gql`
  query employmentDetails($id: ID!) {
    currentPerson {
      id
      employment(id: $id) {
        id
        startedOn
        title
        facility {
          id
          name
          addresses {
            id
            address1
            address2
            city
            postalCode
            state
          }
        }
      }
    }
  }
`;

export const REMOVE_EMPLOYMENT_MUTATION = gql`
  mutation RemoveEmployment(
    $id: ID!
    $endedOn: ISO8601Date!
    $newPrimaryEmploymentId: ID
    $changeStatus: MembershipChangeStatusEnum
  ) {
    removeEmployment(id: $id, endedOn: $endedOn, newPrimaryEmploymentId: $newPrimaryEmploymentId, changeStatus: $changeStatus) {
      message
      success
      contactAddressChanged
      employment {
        id
        person {
          id
        }
        facility {
          id
          name
        }
      }
    }
  }
`;

interface Query {
  currentPerson: {
    employment: {
      title: string;
      startedOn: string;
      facility: {
        name: string;
        addresses: Address[];
      };
    };
  };
}

interface Mutation {
  removeEmployment: {
    success: boolean;
    message: string;
    contactAddressChanged: boolean;
    employment: Employment;
  };
}

const label = "Please provide the employment end date.";

const validate = (startedOn: string) => (date: Moment) => {
  if (date.isBefore(startedOn)) return `End date must be after the start date of ${readableDate(startedOn)}.`;
};

function disassociateFromPerson(cache: ApolloCache<any>, { data: response }: FetchResult<Mutation>) {
  if (!response) return;

  const { id, person } = response.removeEmployment.employment;

  cache.modify({
    id: cache.identify(person),
    fields: {
      employments(previous, { readField }) {
        return previous.filter((ref: Reference) => readField("id", ref) !== id);
      },
    },
  });

  cache.gc();
}

export function ContactChangedNotice({ onChangePreferences }: { onChangePreferences(): void }) {
  const contactEmployment = useReactiveVar(removedContactEmployment);

  function onDismiss() {
    removedContactEmployment(null);
  }

  function changePreferences() {
    onDismiss();
    onChangePreferences();
  }

  return (
    <PopOver visible={!!contactEmployment} onDismiss={onDismiss}>
      <Text className="text-ink-darkest font-700 text-regular-tight mb-2">
        Employment removed, review your contact preferences.
      </Text>

      <Text className="text-regular-normal font-400 text-ink mb-6">
        You have removed your employment at {contactEmployment?.facility.name} which was set as your preferred mailing address.
        Your preferred mailing address has been automatically assigned to your home address. If you would like to assign another
        address, change your preferences.
      </Text>

      <View className="flex-row justify-between">
        <Button size="small" onPress={changePreferences}>
          Change Preferences
        </Button>

        <Button mode="secondary" size="small" onPress={onDismiss}>
          Close
        </Button>
      </View>
    </PopOver>
  );
}

export default function EmploymentRemoveScreen({
  route: {
    params: { id, nextAction, newPrimaryEmploymentId },
  },
  navigation,
}: ScreenProps<"EmploymentRemove">) {
  const [sentForReviewModalVisible, setSentForReviewModalVisible] = useState<boolean>(false);
  const [save, { loading: mutationLoading }] = useMutation<Mutation>(REMOVE_EMPLOYMENT_MUTATION, {
    variables: { id, newPrimaryEmploymentId, changeStatus: nextAction },
  });

  const { data, loading: queryLoading } = useQuery<Query>(EMPLOYMENT_QUERY, {
    variables: { id, newPrimaryEmploymentId },
    fetchPolicy: "cache-first",
  });

  const showSnackBar = useSnackBar();

  const [value, setValue] = useState<string>();
  const [futureDateValue, setFutureDateValue] = useState<string | null>(null);
  const [error, setError] = useState<{ message: string }>();
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState<boolean>(false);
  const [discardChanges, setDiscardChanges] = useState<boolean>(false);

  function onChange(date: string) {
    setValue(date);
  }

  function goBackWeb() {
    if (value) {
      setShowUnsavedChangesModal(true);
      return;
    } else {
      if (window && window.history) {
        return window.history.back();
      }
      navigation.goBack();
    }
  }

  useEffect(() => {
    if (Platform.OS === "web") return;
    if (discardChanges) return navigation.navigate("Account");
    const unsubscribe = navigation.addListener("beforeRemove", (event) => {
      if (value) {
        event.preventDefault();
        setShowUnsavedChangesModal(true);
      }
    });

    return unsubscribe;
  }, [navigation, discardChanges, value]);

  async function onSubmit() {
    if (nextAction === "AddEmployment" && value) {
      navigation.navigate("AddEmployment", {
        employmentsToRemove: [{ id, dateEnded: value }],
        returnTo: () => navigation.navigate("Account"),
      });
      return;
    }

    const { data } = await save({ variables: { endedOn: value, changeStatus: nextAction }, update: disassociateFromPerson });

    if (data?.removeEmployment.success) {
      showSnackBar({ message: "Employment Removed", icon: "check-circle" });
      if (data.removeEmployment.contactAddressChanged) removedContactEmployment(data.removeEmployment.employment);
      if (Object.values(ChangeStatusEnum).includes(nextAction as ChangeStatusEnum)) {
        setSentForReviewModalVisible(true);
        return;
      }
      navigation.navigate("Account");
    } else {
      setError({ message: data?.removeEmployment.message || "Something went wrong." });
    }
  }

  if (queryLoading || mutationLoading || !data) {
    return <ActivityIndicator className="flex-1 items-center justify-center bg-white" />;
  }

  const {
    currentPerson: {
      employment: {
        title,
        startedOn,
        facility: {
          name,
          addresses: [address],
        },
      },
    },
  } = data;

  return (
    <ScreenContainer
      testID="EmploymentRemoveScreen"
      navigationTitle={navigationTitle}
      goBack={() => setShowUnsavedChangesModal(true)}
      overrideBack={goBackWeb}
    >
      <FutureDateEmploymentPopOver {...{ futureDateValue, setFutureDateValue }} />
      <UnsavedChangesModal
        {...{
          mutationLoading: false,
          showUnsavedChangesModal,
          setShowUnsavedChangesModal,
          discardAndExit: () => {
            if (Platform.OS === "web") {
              setShowUnsavedChangesModal(false);
              navigation.navigate("Account");
              return;
            }
            setDiscardChanges(true);
          },
          handleSubmit: () => setShowUnsavedChangesModal(false),
          title: "Exit and discard employment changes?",
          message: "If you exit now, any changes to your employment status will be discarded.",
          buttonLabel: "Continue",
        }}
      />
      <SentForReviewScreen
        visible={sentForReviewModalVisible}
        onClose={() => {
          setSentForReviewModalVisible(false);
          navigation.navigate("AccountEdit");
        }}
        messageText="Your request has been sent to Membership Services for review. Your information will not be updated until approved. Submissions are typically reviewed within 3-7 business days."
      />
      <ScrollableContainer>
        <View className="p-6 justify-between">
          <Text className="text-regular-tight font-700 text-ink-darkest">Employment to Remove</Text>

          <Text className="text-regular-normal font-500 text-ink-darker mt-4">{title}</Text>

          <Text className="text-regular-normal font-400 text-ink mt-2 mb-2">{name}</Text>

          <View>{address && <DisplayAddress address={address} />}</View>
        </View>

        <Divider className="border-[3px] border-sky-lighter" />

        <View className="p-6 justify-between">
          <Text className="text-regular-tight font-700 text-ink-darkest">Effective Date</Text>

          <Text className="text-small-normal font-400 text-ink-dark mt-2.5 mb-6">{label}</Text>

          <DatePickerInput
            {...{ value, title }}
            onChange={(date) => {
              onChange(date);
              setFutureDateValue(getFutureDate(date));
            }}
            testID="date"
            description={label}
            placeholder="End Date"
            validate={validate(startedOn)}
            error={error}
            onBlur={() => {}}
          />
        </View>
      </ScrollableContainer>

      <ActionsContainer>
        <Button onPress={onSubmit} mode="contained" disabled={!value}>
          {nextAction === "AddEmployment" ? "Continue" : "Remove"}
        </Button>
      </ActionsContainer>
    </ScreenContainer>
  );
}
